import { type DataBlockArrayCreator } from './SHURDataBlockCreator'
import { RSKeys, type RSLine } from 'shur-api'
import { fallbackNoDetailsAvailableDataSection } from './SHUREmptyDataCreator'
import { coverageConceptIconData } from './SHURDataboxData'
import type { DataBlock, IconDataSpan } from 'de-zurich-ui-library'

import { t } from '@/i18n'

const deductibleIconDataSpan: IconDataSpan = {
  iconClass: 'icon-vorteile',
  href: 'openModal_deductibleRS',
  isRouterLink: false
}
const propertyIconDataSpan: IconDataSpan = {
  iconClass: 'icon-vorteile',
  href: 'openModal_propertyRS',
  isRouterLink: false
}
const instantCoverageIconData: IconDataSpan = {
  iconClass: 'icon-vorteile',
  href: 'openModal_instantCoverage',
  isRouterLink: false
}

function isProductVersionBefore072022(productVersion: string): boolean {
  if (productVersion === '') return true
  const dateReg = /\d{1,2}.\d{4}/
  if (!dateReg.exec(productVersion)) return false

  // Contract seperated in product version before or after 07.2022
  const refDate = new Date('01 07 2022 00:00:00 GMT')

  const sanitzedString = productVersion.split('.').join(' ')
  const fullDateString = '01 ' + sanitzedString + ' 00:00:00 GMT'

  const parsedContractDate = new Date(fullDateString)

  return parsedContractDate < refDate
}

// Privat- & Berufs-Rechtsschutzversicherung
// LEGAL_PRIVATE_AND_PROFESSIONAL
export class DataLegalPnPCreator implements DataBlockArrayCreator {
  createDatablockArray(rsLine: RSLine): DataBlock[] {
    return [createLegalGlobalData(rsLine)]
  }
}

// Verkehrs-Rechtsschutzversicherung
// LEGAL_ROAD_USERS
export class DataLegalRoadUserCreator implements DataBlockArrayCreator {
  createDatablockArray(rsLine: RSLine): DataBlock[] {
    return [createLegalGlobalData(rsLine)]
  }
}

// Hilfsfunktion für Standard-DataBlocks
function createLegalStandardBlocks(rsLine: RSLine): DataBlock[] {
  return [
    createLegalGlobalData(rsLine),
    createLegalPlace(rsLine),
    createInsuredRisks(rsLine)
  ]
}

// Immobilien-Rechtsschutzversicherung
// LEGAL_PROPERTY_LANDLORD
export class DataLegalPropLandlordCreator implements DataBlockArrayCreator {
  createDatablockArray(rsLine: RSLine): DataBlock[] {
    return createLegalStandardBlocks(rsLine)
  }
}

// Immobilien-Rechtsschutzversicherung
// LEGAL_PROPERTY_OWNER
export class DataLegalPropOwnerCreator implements DataBlockArrayCreator {
  createDatablockArray(rsLine: RSLine): DataBlock[] {
    return createLegalStandardBlocks(rsLine)
  }
}

// Immobilien-Rechtsschutzversicherung
// LEGAL_PROPERTY_TENANT
export class DataLegalPropTenantCreator implements DataBlockArrayCreator {
  createDatablockArray(rsLine: RSLine): DataBlock[] {
    return createLegalStandardBlocks(rsLine)
  }
}

// not used yet
// LEGAL_DRIVER
export class DataLegalDriverCreator implements DataBlockArrayCreator {
  createDatablockArray(rsLine: RSLine): DataBlock[] {
    return createLegalStandardBlocks(rsLine)
  }
}

// not used yet
// LEGAL_PRIVATE_PROFESSIONAL_FAMILY_TRAFFIC
export class DataLegalPPFTCreator implements DataBlockArrayCreator {
  createDatablockArray(rsLine: RSLine): DataBlock[] {
    return createLegalStandardBlocks(rsLine)
  }
}

// not used yet
// LEGAL_FAMILY_TRAFFIC
export class DataLegalFTCreator implements DataBlockArrayCreator {
  createDatablockArray(rsLine: RSLine): DataBlock[] {
    return createLegalStandardBlocks(rsLine)
  }
}

// LEGAL_EXTENDED_CRIMINAL
export class DataLegalExtendedCriminalCreator implements DataBlockArrayCreator {
  createDatablockArray(rsLine: RSLine): DataBlock[] {
    return createLegalStandardBlocks(rsLine)
  }
}

// Versicherungsumfang
function createLegalGlobalData(rsLine: RSLine): DataBlock {
  if (!rsLine.details || rsLine.details.length === 0)
    return fallbackNoDetailsAvailableDataSection()

  const contractDetailsCodeValuePairs = rsLine.details

  return <DataBlock>{
    title: {
      label: t('shurDetails.sections.LEGAL.SCOPE_OF_INSURANCE')
    },
    data: [
      {
        label: t('shurDetails.sections.coverageConcept.title'),
        value: contractDetailsCodeValuePairs.some(
          (entry) => entry.key === RSKeys.CoverageConcept
        )
          ? t(
              `shurDetails.sections.coverageConcept.${
                contractDetailsCodeValuePairs.find(
                  (entry) => entry.key === RSKeys.CoverageConcept
                )!.value
              }`
            )
          : null,
        iconSpan: contractDetailsCodeValuePairs.some(
          (entry) => entry.key === RSKeys.CoverageConcept
        )
          ? coverageConceptIconData(
              String(
                contractDetailsCodeValuePairs.find(
                  (entry) => entry.key === RSKeys.CoverageConcept
                )?.value
              ),
              rsLine.productSubType,
              String(
                contractDetailsCodeValuePairs.find(
                  (entry) => entry.key === RSKeys.ProductVersion
                )?.value
              )
            )
          : null
      },
      {
        label: t(`shurDetails.sections.LEGAL.${RSKeys.TargetGroup}.title`),
        value: contractDetailsCodeValuePairs.some(
          (entry) => entry.key === RSKeys.TargetGroup
        )
          ? t(
              `shurDetails.sections.LEGAL.${RSKeys.TargetGroup}.${
                contractDetailsCodeValuePairs.find(
                  (entry) => entry.key === RSKeys.TargetGroup
                )!.value
              }`
            )
          : null
      },
      {
        label: t(`shurDetails.sections.LEGAL.${RSKeys.ProfessionalLegal}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === RSKeys.ProfessionalLegal
        )?.value
      },
      {
        label: t(`shurDetails.sections.LEGAL.${RSKeys.PropertyLegal}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === RSKeys.PropertyLegal
        )?.value,
        iconSpan: propertyIconDataSpan
      },
      {
        label: t(`shurDetails.sections.${RSKeys.Deductible}.title`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === RSKeys.Deductible
        )?.value,
        iconSpan: isProductVersionBefore072022(
          String(
            contractDetailsCodeValuePairs.find(
              (entry) => entry.key === RSKeys.ProductVersion
            )?.value
          )
        )
          ? null
          : deductibleIconDataSpan
      },
      {
        label: t(`shurDetails.sections.${RSKeys.InstantCoverage}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === RSKeys.InstantCoverage
        )?.value,
        iconSpan: instantCoverageIconData
      },
      {
        label: t(`shurDetails.sections.LEGAL.${RSKeys.CoveredVehicles}.title`),
        value:
          rsLine.productSubType === 'LEGAL_FAMILY_TRAFFIC' &&
          ['tc_Family', 'tc_Single'].includes(
            contractDetailsCodeValuePairs.find(
              (entry) => entry.key === RSKeys.TargetGroup
            )?.value ?? 'null'
          )
            ? t(
                `shurDetails.sections.LEGAL.${RSKeys.CoveredVehicles}.${
                  contractDetailsCodeValuePairs.find(
                    (entry) => entry.key === RSKeys.TargetGroup
                  )?.value
                }`
              )
            : null
      }
    ]
  }
}

// Versicherungsort
function createLegalPlace(rsLine: RSLine): DataBlock {
  if (!rsLine.details || rsLine.details.length === 0)
    return fallbackNoDetailsAvailableDataSection()

  const contractDetailsCodeValuePairs = rsLine.details

  return <DataBlock>{
    title: {
      label: t('shurDetails.sections.insuredPlace.title')
    },
    data: [
      {
        label: t(`shurDetails.sections.insuredPlace.${RSKeys.StreetAndNumber}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === RSKeys.StreetAndNumber
        )?.value
      },
      {
        label: t(`shurDetails.sections.insuredPlace.${RSKeys.ZipcodeAndCity}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === RSKeys.ZipcodeAndCity
        )?.value
      }
    ]
  }
}

// Versicherte Risiken
function createInsuredRisks(rsLine: RSLine): DataBlock {
  if (!rsLine.details || rsLine.details.length === 0)
    return fallbackNoDetailsAvailableDataSection()

  const contractDetailsCodeValuePairs = rsLine.details

  return <DataBlock>{
    title: {
      label: t('shurDetails.sections.insuredRisks')
    },
    data: [
      {
        label: t(`shurDetails.sections.LEGAL.${RSKeys.TotalAnnualGrossRental}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === RSKeys.TotalAnnualGrossRental
        )?.value
      },
      {
        label: t(`shurDetails.sections.LEGAL.${RSKeys.TotalSqm}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === RSKeys.TotalSqm
        )?.value
      },
      {
        label: t(`shurDetails.sections.LEGAL.${RSKeys.NumberOfUnits}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === RSKeys.NumberOfUnits
        )?.value
      },
      {
        label: t(`shurDetails.sections.LEGAL.${RSKeys.RealestateLandlordLegal}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === RSKeys.RealestateLandlordLegal
        )?.value
      },
      {
        label: t(`shurDetails.sections.LEGAL.${RSKeys.RealestateTenantLegal}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === RSKeys.RealestateTenantLegal
        )?.value
      },
      {
        label: t(`shurDetails.sections.LEGAL.${RSKeys.RealestateOwnerLegal}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === RSKeys.RealestateOwnerLegal
        )?.value
      }
    ]
  }
}
